import { KeyboardEvent, MouseEvent, useState } from "react";
import { InformationCardColor, InformationCardProps } from "./types";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

const COLOR_STYLES: Record<InformationCardColor, { container: string; accent: string }> = {
  success: {
    container: "border-cede-success/70 bg-cede-success/25 shadow-[0_10px_40px_rgba(14,177,0,0.25)]",
    accent: "text-cede-success",
  },
  info: {
    container: "border-cede-primary/70 bg-cede-primary/25 shadow-[0_10px_40px_rgba(25,90,174,0.3)]",
    accent: "text-cede-primary",
  },
  warning: {
    container: "border-cede-warning/70 bg-cede-warning/25 shadow-[0_10px_40px_rgba(255,167,38,0.28)]",
    accent: "text-cede-warning",
  },
  error: {
    container: "border-cede-error/70 bg-cede-error/25 shadow-[0_10px_40px_rgba(255,71,87,0.35)]",
    accent: "text-cede-error",
  },
  default: {
    container: "border-cede-border-container bg-cede-bg-component shadow-[0_10px_30px_rgba(0,0,0,0.4)]",
    accent: "text-cede-primary",
  },
};

const getColorStyles = (color: InformationCardColor = "info") => COLOR_STYLES[color] ?? COLOR_STYLES.info;

export const InformationCard = ({
  title,
  content,
  onClose,
  actionElement,
  onClick,
  color = "info",
  id,
  testId,
  className,
}: InformationCardProps) => {
  const [isVisible, setIsVisible] = useState(true);
  if (!isVisible) return null;

  const { container, accent } = getColorStyles(color);
  const primaryContent = title ?? content;
  const secondaryContent = title && content ? content : undefined;

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsVisible(false);
    onClose?.();
  };

  return (
    <div
      id={id}
      data-testid={testId ?? id}
      role="alert"
      onClick={onClick}
      {...(onClick && {
        tabIndex: 0,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      })}
      className={cn(
        "relative w-full overflow-hidden rounded-2xl border px-5 py-4 text-left transition-colors backdrop-blur-md",
        container,
        onClick ? "cursor-pointer hover:border-white/30" : "cursor-default",
        className,
      )}
    >
      <div className="flex w-full items-start gap-4">
        <div className="flex flex-1 flex-col gap-2">
          {primaryContent && (
            <p className={cn("text-base font-semibold text-cede-text-primary", !title && "font-medium")}>
              {primaryContent}
            </p>
          )}
          {secondaryContent && <div className="text-sm text-cede-text-secondary">{secondaryContent}</div>}
        </div>

        {actionElement &&
          (typeof actionElement === "string" ? (
            <span className={cn("text-sm font-medium", accent)} data-testid={`${id || ""}-action`}>
              {actionElement}
            </span>
          ) : (
            <div data-testid={`${id || ""}-action`}>{actionElement}</div>
          ))}

        {onClose && (
          <button
            type="button"
            aria-label="Close notification"
            className="rounded-full p-1 text-cede-text-secondary transition-colors hover:bg-white/5 hover:text-white"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
